/**
 * Runtime detection of which "side" of VerOps the user is hitting:
 *
 * - {@code 'marketing'}  → public website (e.g. {@code verops.com}). Only the
 *   landing page renders here; the auth / dashboards / settings tree is
 *   not even loaded.
 * - {@code 'app'}        → authenticated platform (e.g. {@code app.verops.com}).
 *   No marketing site; root path goes to login → home.
 * - {@code 'dual'}       → single-host fallback for dev / preview
 *   environments that don't have a separate marketing subdomain. The root
 *   path renders the landing page when unauthenticated and the home page
 *   when authenticated (today's behaviour).
 *
 * Detection rules, in priority order:
 *
 * 1. Query-param override {@code ?mode=marketing|app|dual} — for testing.
 *    Persists through a page refresh via {@code sessionStorage} so
 *    in-page navigation doesn't drop the override.
 * 2. Build-time env vars {@code REACT_APP_APP_HOSTNAME_PATTERN} and
 *    {@code REACT_APP_MARKETING_HOSTNAME_PATTERN}.
 * 3. Default: hostname starting with {@code app.} → app, anything else
 *    that contains a dot → marketing, single-token hostnames (localhost,
 *    intranet IPs, .ddns.net dev domain) → dual.
 *
 * NB: this is a static, module-level decision evaluated once on first
 * import. SPAs don't change hostname mid-session so re-evaluating is
 * unnecessary and would make the behaviour harder to reason about.
 */

const SS_KEY = 'verops.deploymentMode.override';

const readOverride = () => {
  try {
    const params = new URLSearchParams(window.location.search);
    const fromUrl = params.get('mode');
    if (fromUrl === 'marketing' || fromUrl === 'app' || fromUrl === 'dual') {
      sessionStorage.setItem(SS_KEY, fromUrl);
      return fromUrl;
    }
    const stored = sessionStorage.getItem(SS_KEY);
    if (stored === 'marketing' || stored === 'app' || stored === 'dual') return stored;
  } catch { /* sessionStorage may be blocked */ }
  return null;
};

const detect = () => {
  if (typeof window === 'undefined') return 'dual'; // SSR / tests

  const override = readOverride();
  if (override) return override;

  const host = window.location.hostname;

  const appPattern = process.env.REACT_APP_APP_HOSTNAME_PATTERN;
  if (appPattern && new RegExp(appPattern).test(host)) return 'app';

  const marketingPattern = process.env.REACT_APP_MARKETING_HOSTNAME_PATTERN;
  if (marketingPattern && new RegExp(marketingPattern).test(host)) return 'marketing';

  // Default heuristic
  if (host.startsWith('app.')) return 'app';

  // A bare-domain or www prefix on a real public domain → marketing.
  // We treat anything with at least one dot AND not a local TLD as
  // marketing-eligible; hosts like {@code localhost} / {@code 127.0.0.1} /
  // {@code afazaa.ddns.net} stay in dual mode.
  const localTokens = ['localhost', '127.0.0.1', '0.0.0.0'];
  if (localTokens.includes(host)) return 'dual';
  if (host.endsWith('.local') || host.endsWith('.ddns.net') || host.endsWith('.lan')) return 'dual';
  // Raw IPv4 / IPv6 address (e.g. 192.168.0.245 dev box, EC2 public IP)
  // → app mode. IPs are practically always dev / preview deploys where
  // the operator wants to log in immediately, not be greeted by a
  // marketing landing page. Returning 'app' here makes `/` resolve to
  // the LoginPage; the marketing tree is not loaded.
  //
  // 'dual' would also bypass the bogus `app.<host>` redirect, but it
  // still parks unauthenticated users on the marketing landing — bad
  // UX for "just let me sign in" dev work.
  if (/^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(host)) return 'app';
  if (host.includes(':')) return 'app';

  // Any other dotted public host (verops.com, www.verops.com,
  // tenant.example.com etc.) defaults to marketing if it doesn't start
  // with {@code app.}. The operator opts into the app side via the
  // {@code app.} subdomain or the env-var pattern above.
  if (host.includes('.')) return 'marketing';

  return 'dual';
};

const MODE = detect();

export const isMarketingMode = () => MODE === 'marketing';
export const isAppMode = () => MODE === 'app';
export const isDualMode = () => MODE === 'dual';
export const getMode = () => MODE;

/**
 * Absolute URL of the platform app for cross-host navigation. Marketing
 * CTAs point here. In dual mode this returns a relative path so dev links
 * stay on the same host.
 */
export const getAppUrl = (path = '/') => {
  if (typeof window === 'undefined') return path;
  if (process.env.REACT_APP_APP_URL) return `${process.env.REACT_APP_APP_URL.replace(/\/$/, '')}${path}`;
  if (MODE === 'dual') return path;
  if (MODE === 'app') return path; // already on app host
  // Marketing → app: derive {@code app.<host>} from current bare host.
  const host = window.location.hostname.replace(/^www\./, '');
  return `${window.location.protocol}//app.${host}${path}`;
};

/**
 * Absolute URL of the marketing website for cross-host navigation.
 * "Back to website" links from inside the app point here.
 */
export const getMarketingUrl = (path = '/') => {
  if (typeof window === 'undefined') return path;
  if (process.env.REACT_APP_MARKETING_URL) return `${process.env.REACT_APP_MARKETING_URL.replace(/\/$/, '')}${path}`;
  if (MODE === 'dual') return path;
  if (MODE === 'marketing') return path; // already on marketing host
  // App → marketing: drop the {@code app.} prefix.
  const host = window.location.hostname.replace(/^app\./, '');
  return `${window.location.protocol}//${host}${path}`;
};
